import { useMemo, useCallback, useState } from 'react';
import { useDebounce } from 'utils';
import { selectAllBrandsModels } from 'store/selectors';
import { useSelector, useDispatch } from 'react-redux';
import { generalActions, targetingPlacementsActions } from 'store/actions';

const checkName = (search, name) => {
  return name.toLowerCase().includes(search.toLowerCase());
};

export const useFacade = (openBrand, includeSet, excludeSet, close) => {
  const dispatch = useDispatch();
  let allBrandsModels = useSelector(selectAllBrandsModels);
  const [search, setSearch] = useState('');
  const debouncedValue = useDebounce(search, 100);
  const brandToCounters = useMemo(
    () =>
      Object.fromEntries(
        allBrandsModels.map(({ brand, models }) => [brand, models.map((m) => m.counter)]),
      ),
    [allBrandsModels],
  );
  const counterToBrand = useMemo(
    () =>
      Object.fromEntries(
        allBrandsModels.flatMap(({ brand, models }) => models.map((m) => [m.counter, brand])),
      ),
    [allBrandsModels],
  );

  const convertDataToSave = useCallback((set) => {
    const arr = [...set];
    let res = arr.map((item) => {
      const isCounter = typeof item === 'number';
      if (!isCounter) return `Brand:${item}`;
      if (isCounter) return `Model:${item}`;
      return;
    });
    return res.toString();
  }, []);

  const brandsWithSelection = useMemo(
    () =>
      allBrandsModels.map((brand) => ({
        isSelected: includeSet.has(brand.brand),
        ...brand,
        models: brand.models.map((model) => ({
          isSelected:
            !excludeSet.has(model.counter) &&
            (includeSet.has(brand.brand) || includeSet.has(model.counter)),
          ...model,
        })),
      })),
    [excludeSet, includeSet, allBrandsModels],
  );

  const faundedBrandsWithSelection = useMemo(() => {
    if (!debouncedValue) return brandsWithSelection;
    const res = brandsWithSelection.reduce(function (acc, brand) {
      const models = brand.models.filter(({ model }) => checkName(debouncedValue, model));
      if (models.length) {
        acc = [...acc, { ...brand, models }];
        openBrand(brand.brand);
        return acc;
      }
      return acc;
    }, []);
    return res;
  }, [debouncedValue, brandsWithSelection]);

  const handleSaveBtn = useCallback(() => {
    const dataToSave = {
      inc: convertDataToSave(includeSet),
      exc: convertDataToSave(excludeSet),
    };
    dispatch(
      generalActions.updateCurrentCampaignObj({
        propField: 'brands_audience_to_save',
        newValue: dataToSave,
      }),
    );
    dispatch(
      targetingPlacementsActions.updateErrorField({ errorField: 'devices', newValue: false }),
    );
    close();
  }, [includeSet, excludeSet]);

  return {
    brandToCounters,
    counterToBrand,
    convertDataToSave,
    brandsWithSelection,
    faundedBrandsWithSelection,
    debouncedValue,
    search,
    setSearch,
    handleSaveBtn,
  };
};
